import React, { useState } from 'react';
import { Button } from '../ui/button';
import { IconButton } from '../ui/IconButton';
import { TextField } from '../ui/TextField';
import ContactSelector from './ContactSelector';
import ContactFlowChart from './ContactFlowChart';
import { 
  FeatherPhone,
  FeatherMail,
  FeatherGlobe,
  FeatherMessageSquare,
  FeatherX,
  FeatherClock,
  FeatherFileText,
  FeatherCheck,
  FeatherAlertCircle,
  FeatherUser,
  FeatherCalendar,
  FeatherPlus,
  FeatherTrendingUp
} from '../ui/icons';
import api from '../../services/api';
import './OneClickContact.css';

const OneClickContact = ({ partner, onSuccess, onCancel, onStatusUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [showQuickForm, setShowQuickForm] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [activeTab, setActiveTab] = useState('phone'); // phone, email, other
  const [showFlowChart, setShowFlowChart] = useState(false);
  const [formData, setFormData] = useState({
    content: '',
    follow_up_required: false,
    follow_up_date: '',
    follow_up_notes: '',
    notes: ''
  });

  // Function to determine company status update based on contact outcome
  // Only specific outcomes should trigger status changes:
  // - interested → in_discussion (positive response)
  // - not_interested → rejected_not_interested (negative response)
  // - wrong_person → contacted_no_response (contact issue)
  // - company_closed → rejected_not_interested (company issue)
  // - permanently_unreachable → rejected_not_interested (contact issue)
  // All other outcomes (no_response, follow_up_required, etc.) keep status as 'contacted'
  const getCompanyStatusFromOutcome = (contactOutcome) => {
    switch (contactOutcome) {
      case 'interested':
        return 'in_discussion';
      case 'not_interested':
        return 'rejected_not_interested';
      case 'wrong_person':
        return 'contacted_no_response';
      case 'company_closed':
        return 'rejected_not_interested';
      case 'permanently_unreachable':
        return 'rejected_not_interested';
      case 'rejected':
        return 'rejected_not_interested';
      default:
        return null; // No status change needed for most outcomes
    }
  };

  // Reorganized scenarios with better categorization and modern UX
  const scenarioGroups = {
    phone: {
      title: "📞 Phone Calls",
      subtitle: "Track your phone conversations",
      scenarios: [
        {
          id: 'called_interested',
          label: 'They\'re Interested',
          subtitle: 'Positive response received',
          icon: <FeatherCheck size={20} />,
          method: 'phone',
          status: 'phone_answered',
          outcome: 'interested',
          content: 'Called and they showed interest in working together.',
          color: 'success',
          priority: 'high'
        },
        {
          id: 'called_not_interested',
          label: 'Not Interested',
          subtitle: 'Declined our offer',
          icon: <FeatherX size={20} />,
          method: 'phone',
          status: 'phone_answered',
          outcome: 'not_interested',
          content: 'Called but they\'re not interested at this time.',
          color: 'danger',
          priority: 'high'
        },
        {
          id: 'called_busy',
          label: 'Phone Busy',
          subtitle: 'Line was engaged',
          icon: <FeatherPhone size={20} />,
          method: 'phone',
          status: 'phone_busy',
          outcome: 'no_response',
          content: 'Called but the phone was busy.',
          followUp: true,
          color: 'warning',
          priority: 'medium'
        },
        {
          id: 'called_no_answer',
          label: 'No Answer',
          subtitle: 'No one picked up',
          icon: <FeatherPhone size={20} />,
          method: 'phone',
          status: 'phone_no_answer',
          outcome: 'no_response',
          content: 'Called but no one answered.',
          followUp: true,
          color: 'warning',
          priority: 'medium'
        },
        {
          id: 'called_back_requested',
          label: 'Call Back Requested',
          subtitle: 'They want us to call later',
          icon: <FeatherClock size={20} />,
          method: 'phone',
          status: 'phone_answered',
          outcome: 'follow_up_required',
          content: 'Called and they requested a call back.',
          followUp: true,
          color: 'info',
          priority: 'high'
        },
        {
          id: 'called_wrong_number',
          label: 'Wrong Number',
          subtitle: 'Incorrect contact details',
          icon: <FeatherAlertCircle size={20} />,
          method: 'phone',
          status: 'phone_answered',
          outcome: 'wrong_person',
          content: 'Called but it was the wrong number.',
          color: 'neutral',
          priority: 'low'
        }
      ]
    },
    email: {
      title: "📧 Email Communications",
      subtitle: "Track your email interactions",
      scenarios: [
        {
          id: 'emailed_sent',
          label: 'Email Sent',
          subtitle: 'Successfully delivered',
          icon: <FeatherMail size={20} />,
          method: 'email',
          status: 'email_sent',
          outcome: 'no_response',
          content: 'Sent email to the company.',
          color: 'info',
          priority: 'medium'
        },
        {
          id: 'emailed_interested',
          label: 'They\'re Interested',
          subtitle: 'Positive email response',
          icon: <FeatherCheck size={20} />,
          method: 'email',
          status: 'email_opened',
          outcome: 'interested',
          content: 'Sent email and they showed interest in working together.',
          color: 'success',
          priority: 'high'
        },
        {
          id: 'emailed_not_interested',
          label: 'Not Interested',
          subtitle: 'Declined via email',
          icon: <FeatherX size={20} />,
          method: 'email',
          status: 'email_opened',
          outcome: 'not_interested',
          content: 'Sent email but they\'re not interested at this time.',
          color: 'danger',
          priority: 'high'
        },
        {
          id: 'emailed_no_response',
          label: 'No Response',
          subtitle: 'Haven\'t heard back',
          icon: <FeatherMail size={20} />,
          method: 'email',
          status: 'email_sent',
          outcome: 'no_response',
          content: 'Sent email but no response received.',
          followUp: true,
          color: 'warning',
          priority: 'medium'
        },
        {
          id: 'emailed_bounced',
          label: 'Email Bounced',
          subtitle: 'Delivery failed',
          icon: <FeatherAlertCircle size={20} />,
          method: 'email',
          status: 'email_bounced',
          outcome: 'email_bounced',
          content: 'Email bounced - invalid email address.',
          color: 'danger',
          priority: 'low'
        },
        {
          id: 'emailed_spam',
          label: 'Marked as Spam',
          subtitle: 'Filtered by email provider',
          icon: <FeatherAlertCircle size={20} />,
          method: 'email',
          status: 'email_sent',
          outcome: 'other',
          content: 'Email was marked as spam or filtered.',
          color: 'neutral',
          priority: 'low'
        }
      ]
    },
    other: {
      title: "🌐 Other Channels",
      subtitle: "Alternative contact methods",
      scenarios: [
        {
          id: 'linkedin_contact',
          label: 'LinkedIn Contact',
          subtitle: 'Professional networking',
          icon: <FeatherGlobe size={20} />,
          method: 'linkedin',
          status: 'sent',
          outcome: 'no_response',
          content: 'Contacted via LinkedIn.',
          color: 'info',
          priority: 'medium'
        },
        {
          id: 'whatsapp_contact',
          label: 'WhatsApp Contact',
          subtitle: 'Instant messaging',
          icon: <FeatherMessageSquare size={20} />,
          method: 'whatsapp',
          status: 'sent',
          outcome: 'no_response',
          content: 'Contacted via WhatsApp.',
          color: 'success',
          priority: 'medium'
        },
        {
          id: 'custom',
          label: 'Custom Contact',
          subtitle: 'Other method or details',
          icon: <FeatherPlus size={20} />,
          method: 'other',
          status: 'sent',
          outcome: 'no_response',
          content: '',
          custom: true,
          color: 'neutral',
          priority: 'low'
        }
      ]
    },
    bypass: {
      title: "🚫 Final Actions",
      subtitle: "When to stop pursuing",
      scenarios: [
        {
          id: 'permanently_unreachable',
          label: 'Permanently Unreachable',
          subtitle: 'After multiple failed attempts',
          icon: <FeatherX size={20} />,
          method: 'other',
          status: 'permanently_unreachable',
          outcome: 'permanently_unreachable',
          content: 'Unable to reach after multiple attempts.',
          color: 'danger',
          priority: 'low'
        },
        {
          id: 'rejected',
          label: 'Rejected',
          subtitle: 'Decided to stop pursuing',
          icon: <FeatherX size={20} />,
          method: 'other',
          status: 'rejected',
          outcome: 'rejected',
          content: 'Decided to stop pursuing this company.',
          color: 'danger',
          priority: 'low'
        }
      ]
    }
  };

  const handleScenarioSelect = (scenario) => {
    if (scenario.custom) {
      setShowQuickForm(true);
      return;
    }

    setSelectedScenario(scenario);
    setFormData({
      content: scenario.content,
      follow_up_required: scenario.followUp || false,
      follow_up_date: scenario.followUp ? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16) : '',
      follow_up_notes: scenario.followUp ? 'Follow up on previous contact' : '',
      notes: ''
    });
  };

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
  };

  const handleQuickContact = async () => {
    if (!selectedScenario) return;
    
    // Ensure a contact is selected
    if (!selectedContact) {
      alert('Please select or add a contact before logging the attempt.');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        partner_id: partner.id,
        contact_method: selectedScenario.method,
        content: formData.content,
        status: selectedScenario.status,
        outcome: selectedScenario.outcome,
        contact_person_name: selectedContact?.name || '',
        contact_person_email: selectedContact?.email || '',
        contact_person_phone: selectedContact?.phone || '',
        contact_person_role: selectedContact?.role || '',
        contact_person_id: selectedContact?.id || null,
        subject: selectedScenario.method === 'email' ? 'Contact from BunnySteps' : '',
        notes: formData.notes,
        follow_up_required: formData.follow_up_required,
        follow_up_date: formData.follow_up_required && formData.follow_up_date ? 
          new Date(formData.follow_up_date).toISOString() : null,
        follow_up_notes: formData.follow_up_notes
      };

      console.log('🔍 Submitting contact attempt data:', JSON.stringify(submitData, null, 2));
      console.log('📊 Selected scenario:', selectedScenario);

      const response = await api.post('/api/contact-attempts', submitData);
      
      // Check if we need to update the company status based on the outcome
      const newCompanyStatus = getCompanyStatusFromOutcome(selectedScenario.outcome);
      if (newCompanyStatus && onStatusUpdate) {
        console.log(`🔄 Contact outcome: "${selectedScenario.outcome}" → Updating company status to: "${newCompanyStatus}"`);
        try {
          await onStatusUpdate(partner.id, newCompanyStatus);
          console.log(`✅ Company status successfully updated to: ${newCompanyStatus}`);
        } catch (statusError) {
          console.error('❌ Error updating company status:', statusError);
          // Don't fail the entire operation if status update fails
        }
      } else if (newCompanyStatus) {
        console.log(`ℹ️ Contact outcome: "${selectedScenario.outcome}" would update status to "${newCompanyStatus}" but onStatusUpdate not available`);
      } else {
        console.log(`ℹ️ Contact outcome: "${selectedScenario.outcome}" - No status change required`);
      }
      
      if (onSuccess) {
        onSuccess(response.data.attempt || response.data);
      }
    } catch (err) {
      console.error('Error logging quick contact:', err);
      // Show error but don't block the flow
    } finally {
      setLoading(false);
    }
  };

  const handleQuickFollowUp = (days) => {
    const followUpDate = new Date();
    followUpDate.setDate(followUpDate.getDate() + days);
    
    setFormData(prev => ({
      ...prev,
      follow_up_required: true,
      follow_up_date: followUpDate.toISOString().slice(0, 16)
    }));
  };

  const handleClose = () => {
    // Reset all states
    setShowQuickForm(false);
    setSelectedScenario(null);
    setSelectedContact(null);
    setShowFlowChart(false);
    setFormData({ content: '', follow_up_required: false, follow_up_date: '', follow_up_notes: '', notes: '' });
    setActiveTab('phone');
    
    // Call the onCancel prop
    if (onCancel) {
      onCancel();
    }
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking the overlay itself, not its children
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Screen 1.5: Flow Chart View
  if (showFlowChart) {
    return (
      <div className="one-click-contact-modern">
        <div className="modern-overlay">
          <div className="modern-header">
            <div className="company-info">
              <h3>📊 Contact History</h3>
              <p className="company-name">{partner.name}</p>
              <p className="subtitle">View contact flow and history</p>
            </div>
            <IconButton
              icon={<FeatherX />}
              onClick={() => setShowFlowChart(false)}
              className="one-click-icon-button"
            />
          </div>
          
          <div className="modern-content">
            <ContactFlowChart
              partnerId={partner.id}
              onClose={() => setShowFlowChart(false)}
              onBack={() => setShowFlowChart(false)}
              embedded={true}
            />
          </div>
        </div>
      </div>
    );
  }

  // Screen 2: Quick Form (Custom Contact)
  if (showQuickForm) {
    return (
      <div className="one-click-contact-modern has-form">
        <div className="modern-form-overlay" onClick={handleOverlayClick}>
          <div className="modern-form-content">
            <div className="modern-form-header">
              <div className="header-content">
                <h4>📝 Custom Contact Details</h4>
                <p>Add specific details about your contact</p>
              </div>
              <IconButton
                icon={<FeatherX />}
                onClick={handleClose}
                className="one-click-icon-button"
              />
            </div>
            
            <div className="modern-form-body">
              <ContactSelector
                partner={partner}
                onSelect={handleContactSelect}
                autoSelect={true}
                selectedContact={selectedContact}
                actionType={null}
                className="one-click-contact-selector"
              />

              <TextField label="What happened?" className="one-click-text-field">
                <TextField.Input
                  as="textarea"
                  placeholder="e.g., Called John, they're interested..."
                  rows={3}
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  autoFocus
                  className="one-click-text-field-input"
                />
              </TextField>

              <div className="modern-followup-section">
                <div className="followup-toggle">
                  <label className="modern-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.follow_up_required}
                      onChange={(e) => setFormData(prev => ({ ...prev, follow_up_required: e.target.checked }))}
                    />
                    <FeatherClock size={16} />
                    Schedule Follow-up
                  </label>
                </div>
                
                {formData.follow_up_required && (
                  <div className="followup-details">
                    <div className="quick-followup-options">
                      <label>Quick Schedule:</label>
                      <div className="one-click-followup-buttons">
                        <button
                          type="button"
                          className="one-click-followup-quick-button"
                          onClick={() => handleQuickFollowUp(1)}
                        >
                          Tomorrow
                        </button>
                        <button
                          type="button"
                          className="one-click-followup-quick-button"
                          onClick={() => handleQuickFollowUp(7)}
                        >
                          Next Week
                        </button>
                        <button
                          type="button"
                          className="one-click-followup-quick-button"
                          onClick={() => handleQuickFollowUp(30)}
                        >
                          Next Month
                        </button>
                      </div>
                    </div>
                    
                    <TextField label="When?" className="one-click-text-field">
                      <TextField.Input
                        type="datetime-local"
                        value={formData.follow_up_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, follow_up_date: e.target.value }))}
                        className="one-click-text-field-input"
                      />
                    </TextField>
                    
                    <TextField label="Follow-up Notes" className="one-click-text-field">
                      <TextField.Input
                        as="textarea"
                        value={formData.follow_up_notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, follow_up_notes: e.target.value }))}
                        placeholder="What to follow up on..."
                        rows={2}
                        className="one-click-text-field-input"
                      />
                    </TextField>
                  </div>
                )}
              </div>
              
              <TextField label="Additional Notes (Optional)" className="one-click-text-field">
                <TextField.Input
                  as="textarea"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional context..."
                  rows={2}
                  className="one-click-text-field-input"
                />
              </TextField>
              
              <div className="modern-form-actions">
                <Button
                  variant="neutral-tertiary"
                  onClick={handleClose}
                  className="one-click-button"
                >
                  Cancel
                </Button>
                <Button
                  variant="brand-primary"
                  onClick={handleQuickContact}
                  disabled={loading}
                  className="one-click-button"
                >
                  {loading ? 'Saving...' : 'Save Contact'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Screen 2: Selected Scenario Form
  if (selectedScenario) {
    return (
      <div className="one-click-contact-modern has-form">
        <div className="modern-form-overlay" onClick={handleOverlayClick}>
          <div className="modern-form-content">
            <div className="modern-form-header">
              <div className="header-content">
                <h4>📋 {selectedScenario.label}</h4>
                <p>What happened during the contact?</p>
              </div>
              <IconButton
                icon={<FeatherX />}
                onClick={handleClose}
                className="one-click-icon-button"
              />
            </div>
            
            <div className="modern-form-body">
              <ContactSelector
                partner={partner}
                onSelect={handleContactSelect}
                autoSelect={true}
                selectedContact={selectedContact}
                actionType={selectedScenario?.method}
                className="one-click-contact-selector"
              />

              <TextField label="What happened during the contact?" className="one-click-text-field">
                <TextField.Input
                  as="textarea"
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Describe what happened (e.g., Spoke with John, they're interested in our services...)"
                  rows={3}
                  autoFocus
                  className="one-click-text-field-input"
                />
              </TextField>

              <div className="modern-followup-section">
                <div className="followup-toggle">
                  <label className="modern-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.follow_up_required}
                      onChange={(e) => setFormData(prev => ({ ...prev, follow_up_required: e.target.checked }))}
                    />
                    <FeatherClock size={16} />
                    Schedule Follow-up
                  </label>
                </div>
                
                {formData.follow_up_required && (
                  <div className="followup-details">
                    <div className="quick-followup-options">
                      <label>Quick Schedule:</label>
                      <div className="one-click-followup-buttons">
                        <button
                          type="button"
                          className="one-click-followup-quick-button"
                          onClick={() => handleQuickFollowUp(1)}
                        >
                          Tomorrow
                        </button>
                        <button
                          type="button"
                          className="one-click-followup-quick-button"
                          onClick={() => handleQuickFollowUp(7)}
                        >
                          Next Week
                        </button>
                        <button
                          type="button"
                          className="one-click-followup-quick-button"
                          onClick={() => handleQuickFollowUp(30)}
                        >
                          Next Month
                        </button>
                      </div>
                    </div>
                    
                    <TextField label="When?" className="one-click-text-field">
                      <TextField.Input
                        type="datetime-local"
                        value={formData.follow_up_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, follow_up_date: e.target.value }))}
                        className="one-click-text-field-input"
                      />
                    </TextField>
                    
                    <TextField label="Follow-up Notes" className="one-click-text-field">
                      <TextField.Input
                        as="textarea"
                        value={formData.follow_up_notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, follow_up_notes: e.target.value }))}
                        placeholder="What to follow up on..."
                        rows={2}
                        className="one-click-text-field-input"
                      />
                    </TextField>
                  </div>
                )}
              </div>
              
              <TextField label="Additional Notes (Optional)" className="one-click-text-field">
                <TextField.Input
                  as="textarea"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional context..."
                  rows={2}
                  className="one-click-text-field-input"
                />
              </TextField>
              
              <div className="modern-form-actions">
                <Button
                  variant="neutral-tertiary"
                  onClick={() => setSelectedScenario(null)}
                  className="one-click-button"
                >
                  Back
                </Button>
                <div style={{ position: 'relative' }}>
                  <Button
                    variant="brand-primary"
                    onClick={handleQuickContact}
                    disabled={loading || !selectedContact}
                    className="one-click-button"
                    title={!selectedContact ? 'Please select or add a contact to proceed' : ''}
                  >
                    {loading ? 'Logging...' : 'Log Contact Attempt'}
                  </Button>
                  {!selectedContact && !loading && (
                    <div style={{
                      fontSize: '12px',
                      color: '#ef4444',
                      marginTop: '4px',
                      textAlign: 'center'
                    }}>
                      Contact required
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Screen 1: Modern Scenario Selection
  return (
    <div className="one-click-contact-modern" onClick={handleOverlayClick}>
      <div className="modern-overlay">
        <div className="modern-header">
          <div className="company-info">
            <h3>📞 Quick Contact Log</h3>
            <p className="company-name">{partner.name}</p>
            <p className="subtitle">Select your contact scenario to log the interaction</p>
          </div>
          <div className="header-actions">
            <Button
              variant="neutral-tertiary"
              onClick={() => setShowFlowChart(true)}
              title="View Contact History"
              className="one-click-button"
            >
              <FeatherTrendingUp size={16} />
              History
            </Button>
            <IconButton
              icon={<FeatherX />}
              onClick={handleClose}
              className="one-click-icon-button"
            />
          </div>
        </div>

        <div className="modern-tabs">
          {Object.entries(scenarioGroups).map(([key, group]) => (
            <button
              key={key}
              className={`modern-tab ${activeTab === key ? 'active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              <span className="tab-icon">{group.title.split(' ')[0]}</span>
              <span className="tab-label">{group.title.split(' ').slice(1).join(' ')}</span>
            </button>
          ))}
        </div>

        <div className="modern-content">
          <div className="group-header">
            <h4>{scenarioGroups[activeTab].title}</h4>
            <p>{scenarioGroups[activeTab].subtitle}</p>
          </div>

          <div className="modern-scenarios-grid">
            {scenarioGroups[activeTab].scenarios.map((scenario) => (
              <button
                key={scenario.id}
                className={`modern-scenario-card ${scenario.color} ${scenario.priority} ${loading ? 'loading' : ''}`}
                onClick={() => handleScenarioSelect(scenario)}
                disabled={loading}
              >
                <div className="scenario-icon">
                  {scenario.icon}
                </div>
                <div className="scenario-content">
                  <div className="scenario-label">{scenario.label}</div>
                  <div className="scenario-subtitle">{scenario.subtitle}</div>
                </div>
                {scenario.followUp && (
                  <div className="followup-badge">
                    <FeatherClock size={12} />
                  </div>
                )}
                {loading && <div className="modern-loading-spinner"></div>}
              </button>
            ))}
          </div>
        </div>

        <div className="modern-footer">
          <div className="keyboard-tips">
            <span>💡 Use Tab + Enter to navigate quickly</span>
          </div>
          <div className="company-stats">
            <span>📊 {partner.name}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OneClickContact; 